import { MODEL_SETTINGS_QUERY_KEY } from '@/components/admin/reglages/model-settings-editor';
import { useAdminSession } from '@/lib/admin-session';
import { fetchExperiences, fetchModelCatalog, fetchModelSettings } from '@/lib/api';
import { useReplaySettings } from '@/lib/use-replay-settings';
import type {
  ExperienceList,
  ModelCatalogView,
  ModelSettingsDetail,
  ReplaySettings,
} from '@matter_extractor/shared';
import { MODEL_SETTINGS_ID } from '@matter_extractor/shared';
import { useQuery } from '@tanstack/react-query';

/**
 * What the launch box reads to fill its choices (npc.md §6.1): the experiences to play, the model
 * of the player Réglages > Rejeu holds, the catalog that offers another model for this launch (N7)
 * and the history of Réglages > Modèles that offers another version (N8).
 *
 * Each read answers on its own and none blocks the box: a catalog that does not come only leaves
 * the player's model to its default, and a history that does not come leaves the models in force.
 * The keys are the ones the screens that own these reads write, so the box rides their cache.
 */
export interface LaunchOptions {
  readonly experiences: ExperienceList | undefined;
  readonly experiencesFailed: boolean;
  readonly settings: ReplaySettings | null | undefined;
  readonly catalog: ModelCatalogView | undefined;
  readonly models: ModelSettingsDetail | undefined;
}

export function useLaunchOptions(): LaunchOptions {
  const { email } = useAdminSession();
  const experiences = useQuery({
    queryKey: ['experiences', email],
    queryFn: fetchExperiences,
    retry: false,
  });
  const settings = useReplaySettings();
  const catalog = useQuery({
    queryKey: ['model-catalog', email],
    queryFn: fetchModelCatalog,
    retry: false,
  });
  const models = useQuery({
    queryKey: [MODEL_SETTINGS_QUERY_KEY, email, MODEL_SETTINGS_ID],
    queryFn: fetchModelSettings,
    retry: false,
  });
  return {
    experiences: experiences.data,
    experiencesFailed: experiences.isError,
    settings: settings.isError ? null : settings.data,
    catalog: catalog.data,
    models: models.data,
  };
}
